import { parseFieldExpression, expressionLiteral } from "../fieldExpression";
import { textModifiers } from "./text";
import { numberModifiers } from "./number";
import { formatDate } from "./formatDate";

export interface PreviewValue {
    value: unknown;
    type?: string;
    found: boolean;
}

export interface PreviewModifier {
    types?: string[];
    apply: (value: unknown, args: unknown[]) => unknown;
}

export const unsupportedPreview = Symbol("unsupported field modifier preview");

export function isEmptyModifierValue(value: unknown): boolean {
    if (value == null) return true;
    if (typeof value === "string") return value.trim() === "";
    return Array.isArray(value) && value.length === 0;
}

export function modifierString(value: unknown): string | undefined {
    if (value == null || typeof value === "object") return undefined;
    if (typeof value === "boolean") return value ? "1" : "";
    return String(value);
}

const modifiers: Record<string, PreviewModifier> = {
    ...textModifiers,
    ...numberModifiers,
    default: {
        apply: (value, args) =>
            isEmptyModifierValue(value) ? (args[0] ?? null) : value,
    },
    format_date: {
        apply: (value, args) => formatDate(value, modifierString(args[0])),
    },
};

function resolveArgument(
    argument: string,
    resolve: (key: string) => PreviewValue,
    depth: number,
): PreviewValue {
    if (argument.startsWith("(") && argument.endsWith(")")) {
        return evaluateFieldExpression(
            argument.slice(1, -1),
            resolve,
            depth + 1,
        );
    }
    const literal = expressionLiteral(argument);
    if (literal)
        return { value: literal.value, type: literal.type, found: true };
    return resolve(argument);
}

function hasBalancedTokens(expression: string): boolean {
    let depth = 0;
    for (const token of expression.match(/{{|}}/g) ?? []) {
        depth += token === "{{" ? 1 : -1;
        if (depth < 0) return false;
    }
    return depth === 0;
}

export function evaluateFieldExpression(
    expression: string,
    resolve: (key: string) => PreviewValue,
    depth = 0,
): PreviewValue {
    const parsed = parseFieldExpression(expression);
    if (!parsed || depth > 20 || !hasBalancedTokens(expression))
        return { value: unsupportedPreview, found: false };
    const subject = resolveArgument(parsed.key, resolve, depth);
    let value = subject.value;
    if (
        !subject.found &&
        !parsed.modifiers.some((modifier) => modifier.name === "default")
    )
        return subject;

    for (const modifier of parsed.modifiers) {
        const handler = Object.hasOwn(modifiers, modifier.name)
            ? modifiers[modifier.name]
            : undefined;
        if (!handler || value === unsupportedPreview)
            return { value: unsupportedPreview, found: false };
        if (handler.types && !handler.types.includes(subject.type ?? "null"))
            continue;
        const args = modifier.arguments.map(
            (arg) => resolveArgument(arg, resolve, depth).value,
        );
        if (args.includes(unsupportedPreview))
            return { value: unsupportedPreview, found: false };
        value = handler.apply(value, args);
    }
    return { value, type: subject.type, found: value !== unsupportedPreview };
}
